import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";

const AUTH_TYPE_INFO = {
    bearer: { label: "Bearer Token (API Key)", icon: "Key", fields: [{ key: "apiKey", label: "API Key", type: "password" }] },
    basic: { label: "Basic Auth", icon: "Lock", fields: [{ key: "username", label: "Username", type: "text" }, { key: "password", label: "Password", type: "password" }] },
    oauth2: { label: "OAuth 2.0", icon: "Shield", fields: [{ key: "clientId", label: "Client ID", type: "text" }, { key: "clientSecret", label: "Client Secret", type: "password" }] },
    api_key: { label: "API Key", icon: "FileKey", fields: [{ key: "apiKey", label: "API Key", type: "password" }] },
    none: { label: "No Authentication", icon: "Unlock", fields: [] },
};

function getFieldsFromAuth(auth) {
    if (!auth) return AUTH_TYPE_INFO.bearer.fields;

    // Use dynamic credentialFields from backend if available
    if (auth.credentialFields && auth.credentialFields.length > 0) {
        return auth.credentialFields.map((cf) => ({
            key: cf.name,
            label: cf.label,
            type: cf.type || "password",
        }));
    }

    // Fall back to static mapping
    const config = AUTH_TYPE_INFO[auth.type];
    return config ? config.fields : AUTH_TYPE_INFO.bearer.fields;
}

export default function AuthConfigPanel({ authType, auth, credentials, onChange, onTest, onSkipToPublish, isTaskRunning }) {
    const { t } = useTranslation();
    const resolvedType = auth?.type || authType || "bearer";
    const config = AUTH_TYPE_INFO[resolvedType] || AUTH_TYPE_INFO.bearer;
    const fields = getFieldsFromAuth(auth);
    const isFilled = fields.length === 0 || fields.every((f) => credentials[f.key]);

    return (
        <div className="bg-white border-2 border-blue-200 rounded-xl overflow-hidden shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="bg-blue-600 px-5 py-3 flex items-center gap-3">
                <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                    <h3 className="text-white font-semibold text-sm">{t("builder.auth.enterCredentials")}</h3>
                    <p className="text-blue-200 text-xs">{t("builder.auth.detected", { type: config.label })}</p>
                </div>
            </div>

            <div className="p-5 space-y-5">
                <div className="flex items-center gap-3 bg-blue-50 rounded-lg p-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Icon name={config.icon} className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">{t("builder.auth.authentication")}: {config.label}</p>
                        <p className="text-xs text-blue-600">{t("builder.auth.detectedFromDocs")}</p>
                    </div>
                    <Icon name="CheckCircle" className="w-5 h-5 text-blue-500" />
                </div>

                <div className="space-y-3">
                    {fields.map((field) => (
                        <div key={field.key}>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">{field.label}</label>
                            <input
                                type={field.type}
                                value={credentials[field.key] || ""}
                                onChange={(e) => onChange({ ...credentials, [field.key]: e.target.value })}
                                placeholder={t("builder.auth.enterField", { field: field.label.toLowerCase() })}
                                className="w-full px-4 py-3 border-2 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
                            />
                        </div>
                    ))}
                </div>

                <button
                    onClick={onTest}
                    disabled={!isFilled || isTaskRunning}
                    className={`w-full py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${isFilled && !isTaskRunning ? "bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-200" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                >
                    {isTaskRunning ? (
                        <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                    ) : (
                        <Icon name="Play" className="w-4 h-4" />
                    )}
                    {isTaskRunning
                        ? t("builder.auth.waitingForBuild", { defaultValue: "Waiting for build to finish…" })
                        : fields.length === 0
                        ? t("builder.auth.startNoAuth")
                        : t("builder.auth.startTesting")}
                </button>

                {isTaskRunning && (
                    <p className="text-xs text-center text-gray-500">
                        {t("builder.auth.fillWhileWaiting", { defaultValue: "You can fill in your credentials while the AI finishes discovering endpoints." })}
                    </p>
                )}

                {!isTaskRunning && !isFilled && fields.length > 0 && (
                    <p className="text-xs text-center text-gray-400">
                        {t("builder.auth.enterAbove")}
                    </p>
                )}

                {onSkipToPublish && (
                    <button
                        type="button"
                        onClick={onSkipToPublish}
                        className="w-full py-2 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors flex items-center justify-center gap-1.5"
                    >
                        <Icon name="FastForward" className="w-3.5 h-3.5" />
                        {t("builder.auth.skipToPublish", { defaultValue: "Skip testing — publish as-is" })}
                    </button>
                )}
            </div>
        </div>
    );
}
